import React, { useState } from "react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { DownloadSimple, X, ShareNetwork, Plus, DotsThreeVertical, CheckCircle } from "@phosphor-icons/react";

// Bottom-sheet with manual install instructions for browsers without a native prompt.
function InstallSheet({ open, onClose, isIOS }) {
  if (!open) return null;
  const iosSteps = [
    { icon: ShareNetwork, t: "Tap the Share button", d: "In the Safari toolbar at the bottom of the screen." },
    { icon: Plus, t: "Select “Add to Home Screen”", d: "Scroll down the share menu to find it." },
    { icon: CheckCircle, t: "Tap “Add”", d: "iYeneh will appear on your home screen like an app." },
  ];
  const otherSteps = [
    { icon: DotsThreeVertical, t: "Open your browser menu", d: "Tap the ⋮ or ⋯ icon in Chrome or Edge." },
    { icon: DownloadSimple, t: "Choose “Install app”", d: "Or “Add to Home screen” / “Install iYeneh”." },
    { icon: CheckCircle, t: "Confirm install", d: "iYeneh opens like a real app, no App Store needed." },
  ];
  const steps = isIOS ? iosSteps : otherSteps;
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center" data-testid="install-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-t-3xl border border-white/10 bg-[#0F1729] p-6 pb-8 text-white shadow-2xl sm:rounded-3xl">
        <button onClick={onClose} data-testid="install-sheet-close" className="absolute right-4 top-4 text-white/50 hover:text-white"><X size={22} /></button>
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#10B981]"><DownloadSimple size={26} weight="fill" /></div>
        <h3 style={{ fontFamily: "'Outfit',sans-serif" }} className="text-xl font-extrabold">Install iYeneh {isIOS ? "on iPhone" : "on this device"}</h3>
        <p className="mt-1 text-sm text-white/60">Add iYeneh to your home screen for one-tap access to calls, wallet and services.</p>
        <div className="mt-5 space-y-3">
          {steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FF7A00]/15 text-[#FF7A00]"><s.icon size={20} weight="bold" /></div>
              <div><p className="text-sm font-bold">{i + 1}. {s.t}</p><p className="text-xs text-white/55">{s.d}</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Reusable install trigger. Hides itself when the app is already installed.
export function InstallButton({ className = "", style, children = "Install iYeneh", testid = "install-pwa", icon = true }) {
  const { canInstall, isIOS, isStandalone, promptInstall } = usePWAInstall();
  const [sheet, setSheet] = useState(false);
  if (isStandalone) return null;

  const onClick = async () => {
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome !== "accepted") return;
    } else {
      setSheet(true);
    }
  };

  return (
    <>
      <button data-testid={testid} onClick={onClick} className={className} style={style}>
        {icon && <DownloadSimple size={18} weight="bold" />} {children}
      </button>
      <InstallSheet open={sheet} onClose={() => setSheet(false)} isIOS={isIOS} />
    </>
  );
}
